import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getAgentLocationsLatest, getAgentLocationHistory } from '../services/api';

// Restore default marker icons broken by webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface DeviceInfo {
  platform?: string | null;
  vendor?: string | null;
  model?: string | null;
  language?: string | null;
  screen?: string | null;
  cores?: number | null;
  memory?: number | null;
  connection?: string | null;
  downlink?: number | null;
  battery?: number | null;
  charging?: boolean | null;
  tz?: string | null;
  online?: boolean | null;
}

interface AgentPos {
  id: number;
  user_id: string | null;
  phone: string | null;
  latitude: number;
  longitude: number;
  accuracy: number | null;
  altitude: number | null;
  speed: number | null;
  source: string | null;
  created_at: string;
  prenom: string | null;
  nom: string | null;
  telephone: string | null;
  email: string | null;
  device_info: DeviceInfo | null;
  user_agent: string | null;
}

function deviceSummary(d: DeviceInfo | null): string {
  if (!d) return '';
  const parts: string[] = [];
  if (d.model) parts.push(d.model);
  if (d.platform) parts.push(d.platform);
  if (d.connection) parts.push(d.connection.toUpperCase());
  if (typeof d.battery === 'number') parts.push(`${d.battery}%${d.charging ? '⚡' : ''}`);
  return parts.join(' · ');
}

function labelOf(p: AgentPos): string {
  const nom = `${p.prenom || ''} ${p.nom || ''}`.trim();
  if (nom) return nom;
  if (p.phone) return `+${p.phone} (non-identifie)`;
  if (p.email) return p.email;
  return 'Anonyme';
}

const AgentPositions: React.FC = () => {
  const [positions, setPositions] = useState<AgentPos[]>([]);
  const [loading, setLoading] = useState(false);
  const [hours, setHours] = useState<number>(24);
  const [selected, setSelected] = useState<AgentPos | null>(null);
  const [history, setHistory] = useState<AgentPos[]>([]);
  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstance = useRef<L.Map | null>(null);
  const markersLayer = useRef<L.LayerGroup | null>(null);

  const load = () => {
    setLoading(true);
    getAgentLocationsLatest(hours)
      .then(r => setPositions(r.data.data || []))
      .catch(() => setPositions([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [hours]);

  // Poll every 30s so the admin sees fresh positions without a refresh
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [hours]);

  // Init Leaflet once
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;
    const map = L.map(mapRef.current).setView([14.6928, -17.4467], 7); // Dakar default
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
    }).addTo(map);
    markersLayer.current = L.layerGroup().addTo(map);
    mapInstance.current = map;
  }, []);

  // Refresh markers when positions change
  useEffect(() => {
    if (!mapInstance.current || !markersLayer.current) return;
    markersLayer.current.clearLayers();

    const bounds: L.LatLngBoundsExpression = [];
    positions.forEach(p => {
      const lat = Number(p.latitude);
      const lng = Number(p.longitude);
      if (isNaN(lat) || isNaN(lng)) return;
      const label = labelOf(p);
      const marker = L.marker([lat, lng]).addTo(markersLayer.current!);
      const displayPhone = p.telephone || p.phone;
      const sourceBadge = p.source === 'foonek' ? '<span style="background:#FFF3E0;color:#E65100;padding:1px 6px;border-radius:8px;font-size:10px;margin-left:4px">foonek</span>' : '';
      const devSum = deviceSummary(p.device_info);
      marker.bindPopup(
        `<div style="font-size:12px;min-width:200px">
           <strong>${escape(label)}</strong>${sourceBadge}<br/>
           ${displayPhone ? escape(displayPhone) + '<br/>' : ''}
           <em>${new Date(p.created_at).toLocaleString('fr-FR')}</em><br/>
           Precision: ${p.accuracy ? Math.round(Number(p.accuracy)) + ' m' : '-'}<br/>
           ${devSum ? '<div style="margin-top:6px;padding-top:6px;border-top:1px solid #eee;color:#555">' + escape(devSum) + '</div>' : ''}
           <div style="margin-top:6px"><a href="https://www.google.com/maps?q=${lat},${lng}" target="_blank">Ouvrir dans Google Maps</a></div>
         </div>`
      );
      if (p.accuracy && Number(p.accuracy) > 0) {
        L.circle([lat, lng], { radius: Number(p.accuracy), color: '#0288d1', weight: 1, fillOpacity: 0.1 }).addTo(markersLayer.current!);
      }
      bounds.push([lat, lng]);
    });
    if (bounds.length > 0) {
      mapInstance.current.fitBounds(bounds as L.LatLngBoundsExpression, { padding: [40, 40], maxZoom: 14 });
    }
  }, [positions]);

  const escape = (s: string) => String(s || '').replace(/[<>&"]/g, c =>
    ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' } as Record<string, string>)[c]);

  const openHistory = async (p: AgentPos) => {
    setSelected(p);
    setHistory([]);
    // Anonymous (foonek) entries don't have a user_id, so no per-user history —
    // we surface only the current point via the marker on the map.
    if (!p.user_id) return;
    try {
      const r = await getAgentLocationHistory(p.user_id, 50);
      setHistory(r.data.data || []);
    } catch {
      setHistory([]);
    }
  };

  const fmt = (d: string) => {
    try { return new Date(d).toLocaleString('fr-FR'); }
    catch { return d; }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, color: '#0277bd' }}>Positions agents</h2>
          <p style={{ margin: '4px 0 0', color: '#888', fontSize: 13 }}>Dernieres positions envoyees par les agents terrain (auto-refresh 30s)</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <label style={{ fontSize: 13, color: '#666' }}>Fenetre :</label>
          <select
            value={hours}
            onChange={e => setHours(parseInt(e.target.value, 10))}
            style={{ padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}
          >
            <option value={1}>1 heure</option>
            <option value={6}>6 heures</option>
            <option value={24}>24 heures</option>
            <option value={72}>3 jours</option>
            <option value={168}>7 jours</option>
            <option value={720}>30 jours</option>
          </select>
          <button
            onClick={load}
            disabled={loading}
            style={{
              padding: '8px 14px', border: 'none', borderRadius: 6, background: '#0288d1', color: '#fff',
              fontSize: 13, fontWeight: 700, cursor: loading ? 'wait' : 'pointer',
            }}
          >{loading ? '...' : 'Rafraichir'}</button>
        </div>
      </div>

      {/* Short public link banner */}
      <div style={{
        background: 'linear-gradient(90deg,#1565C0,#0288d1)', color: '#fff', borderRadius: 10,
        padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        <div style={{ fontSize: 22 }}>&#128225;</div>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 12, opacity: 0.85 }}>Lien court a partager aux agents (sans installation)</div>
          <div style={{ fontSize: 15, fontFamily: 'monospace', fontWeight: 700 }}>https://forgest-senegal.com/foonek</div>
        </div>
        <button
          onClick={() => { navigator.clipboard.writeText('https://forgest-senegal.com/foonek').then(() => alert('Lien copie')); }}
          style={{ padding: '8px 14px', border: 'none', borderRadius: 6, background: '#fff', color: '#0277bd', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}
        >Copier</button>
        <a
          href={`https://wa.me/?text=${encodeURIComponent('Bonjour, cliquez sur ce lien pour partager votre position: https://forgest-senegal.com/foonek')}`}
          target="_blank" rel="noreferrer"
          style={{ padding: '8px 14px', borderRadius: 6, background: '#25D366', color: '#fff', fontWeight: 700, textDecoration: 'none', fontSize: 13 }}
        >Partager WhatsApp</a>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 16, alignItems: 'stretch' }}>
        {/* Map */}
        <div style={{
          background: '#fff', borderRadius: 10, overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)', height: 560,
        }}>
          <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
        </div>

        {/* List */}
        <div style={{
          background: '#fff', borderRadius: 10, padding: 14,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)', maxHeight: 560, overflowY: 'auto',
        }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#333' }}>
            Agents ({positions.length})
          </h3>
          {loading && positions.length === 0 && (
            <div style={{ textAlign: 'center', padding: 30, color: '#999' }}>Chargement...</div>
          )}
          {!loading && positions.length === 0 && (
            <div style={{ textAlign: 'center', padding: 30, color: '#999', fontSize: 13 }}>
              Aucune position recue dans cette fenetre.<br/>
              Envoyez un lien de localisation depuis la page Utilisateurs.
            </div>
          )}
          {positions.map(p => {
            const isSelected = selected && ((selected.user_id && selected.user_id === p.user_id) || (!selected.user_id && selected.phone === p.phone));
            const isAnon = !p.user_id;
            return (
              <div
                key={p.id}
                onClick={() => openHistory(p)}
                style={{
                  padding: 10, borderBottom: '1px solid #f0f0f0', cursor: 'pointer',
                  background: isSelected ? '#E3F2FD' : 'transparent',
                  borderRadius: 4,
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, color: isAnon ? '#E65100' : '#0277bd', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {labelOf(p)}
                  {p.source === 'foonek' && (
                    <span style={{ background: '#FFF3E0', color: '#E65100', padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700 }}>foonek</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: '#666', marginTop: 3 }}>
                  {fmt(p.created_at)} {p.accuracy ? ` · ~${Math.round(Number(p.accuracy))} m` : ''}
                </div>
                <div style={{ fontSize: 11, color: '#999', marginTop: 3, fontFamily: 'monospace' }}>
                  {Number(p.latitude).toFixed(5)}, {Number(p.longitude).toFixed(5)}
                </div>
                {p.device_info && (
                  <div style={{ fontSize: 10, color: '#0277bd', marginTop: 4, background: '#F0F8FF', padding: '3px 6px', borderRadius: 4 }}>
                    {deviceSummary(p.device_info) || 'appareil inconnu'}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* History drawer */}
      {selected && (
        <div style={{
          background: '#fff', borderRadius: 10, padding: 16, marginTop: 16,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 15, color: '#333' }}>
              Historique · {`${selected.prenom || ''} ${selected.nom || ''}`.trim()} ({history.length})
            </h3>
            <button
              onClick={() => setSelected(null)}
              style={{ border: '1px solid #ddd', background: '#fff', padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}
            >Fermer</button>
          </div>
          {history.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 20, color: '#999', fontSize: 13 }}>Aucun historique</div>
          ) : (
            <table className="data-table" style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Latitude</th>
                  <th>Longitude</th>
                  <th>Precision</th>
                  <th>IP</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.id}>
                    <td>{fmt(h.created_at)}</td>
                    <td style={{ fontFamily: 'monospace' }}>{Number(h.latitude).toFixed(6)}</td>
                    <td style={{ fontFamily: 'monospace' }}>{Number(h.longitude).toFixed(6)}</td>
                    <td>{h.accuracy ? `${Math.round(Number(h.accuracy))} m` : '-'}</td>
                    <td style={{ fontSize: 11, color: '#999' }}>{(h as any).ip || '-'}</td>
                    <td>
                      <a
                        href={`https://www.google.com/maps?q=${h.latitude},${h.longitude}`}
                        target="_blank" rel="noreferrer"
                        style={{ color: '#0277bd', fontSize: 12 }}
                      >Google Maps</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default AgentPositions;
